import { useEffect, useState } from 'react';

import { useProfile } from '@/features/profile/hooks/useProfile';

type ResolvedTheme = 'dark' | 'light' | 'sepia';
type FontSize = 'small' | 'medium' | 'large' | 'extra-large';

export interface ReaderTheme {
  theme: ResolvedTheme;
  fontSize: FontSize;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function useReaderTheme(): ReaderTheme {
  const { data: profile } = useProfile();
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Listen for OS color scheme changes
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'light' : 'dark');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const themePref = profile?.reader?.theme ?? 'system';
  const resolvedTheme: ResolvedTheme = themePref === 'system' ? systemTheme : themePref;
  const fontSize: FontSize = profile?.reader?.fontSize ?? 'medium';

  return { theme: resolvedTheme, fontSize };
}
